import { useRef } from 'react';
import { IPost, IProduct, IProductLayoutSection } from '@monofront/types';
import { useProductLayout } from '@monofront/business/hooks';
import {
  SlicesContainer,
  SlicesContainerLayoutType,
} from '@screens/SlicesContainer';
import ProductCoCreation from '../ProductCoCreation';
import ProductPresentation from '../ProductPresentation';
import ProductVendor from '../ProductVendor';
import ProductAdvices from '../ProductAdvices';
import ProductReviews from '../ProductReviews';
import ProductStickyMenu from '../ProductStickyMenu';
import { useTagContextMatches } from '@hooks/businessContainer';

interface IProductDetailsProps {
  product: IProduct;
  tips?: IPost[];
}

const ProductDetails: React.FC<IProductDetailsProps> = ({ product, tips }) => {
  const { productLayout } = useProductLayout();
  const refs = {
    presentation: useRef<HTMLHeadingElement>(null),
    cocreation: useRef<HTMLHeadingElement>(null),
    advices: useRef<HTMLHeadingElement>(null),
    vendor: useRef<HTMLHeadingElement>(null),
    reviews: useRef<HTMLHeadingElement>(null),
  };

  const slices = productLayout.slices.length
    ? productLayout.slices.slice(0, productLayout.slices_before_desc || 0)
    : [];
  const sectionOrdering = productLayout.section_ordering;

  const { tagContextMatches } = useTagContextMatches();

  // filter sections that has conditions and add associated refs
  let filteredSectionOrdering = sectionOrdering.reduce<IProductLayoutSection[]>(
    (sectionList, section) => {
      const isSectionDisplayed = section.condition
        ? tagContextMatches(section.condition, {
            tags: { product: product.tags },
          })
        : true;

      if (isSectionDisplayed) {
        return [
          ...sectionList,
          {
            ...section,
            ref: refs[section.section_id],
          },
        ];
      }

      return sectionList;
    },
    [],
  );

  // filter eventual duplicated sections (=> edge-case: prismic editor mistakes)
  filteredSectionOrdering = filteredSectionOrdering.filter((section, index) => {
    const sectionPosition = filteredSectionOrdering.findIndex(
      ({ section_id }) => section_id === section.section_id,
    );

    return sectionPosition === index;
  });

  return (
    <>
      {slices.length > 0 ? (
        <SlicesContainer
          layout={SlicesContainerLayoutType.CLEAR}
          pageContents={slices}
          UNSAFE_className="[&_.slice-container]:tw-mt-8"
        />
      ) : null}
      <ProductStickyMenu sections={filteredSectionOrdering} product={product} />
      <div className="product-details tw-mt-5 desktop:tw-mx-auto desktop:tw-mt-7 desktop:tw-max-w-[800px]">
        {filteredSectionOrdering.map((section, index) => {
          switch (section.section_id) {
            case 'presentation':
              return (
                <ProductPresentation
                  ref={refs.presentation}
                  name={{
                    sectionTitle: section.title,
                    menuTitle: section.label_menu,
                  }}
                  product={product}
                  key={index}
                />
              );
            case 'cocreation':
              return (
                <ProductCoCreation
                  ref={refs.cocreation}
                  name={{
                    sectionTitle: section.title,
                    menuTitle: section.label_menu,
                  }}
                  product={product}
                  key={index}
                />
              );
            case 'advices':
              return (
                <ProductAdvices
                  ref={refs.advices}
                  name={{
                    sectionTitle: section.title,
                    menuTitle: section.label_menu,
                  }}
                  product={product}
                  tips={tips}
                  key={index}
                />
              );
            case 'vendor':
              return (
                <ProductVendor
                  ref={refs.vendor}
                  name={{
                    sectionTitle: section.title,
                    menuTitle: section.label_menu,
                  }}
                  product={product}
                  key={index}
                />
              );
            case 'reviews':
              return (
                <ProductReviews
                  ref={refs.reviews}
                  name={{
                    sectionTitle: section.title,
                    menuTitle: section.label_menu,
                  }}
                  product={product}
                  key={index}
                />
              );
            default:
              return null;
          }
        })}
      </div>
    </>
  );
};

export default ProductDetails;
